import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, Clock, Lock, Sparkles } from 'lucide-react';
import { TeamLogo } from '@/components/TeamLogo';
import { RevealHighlight } from '@/components/RevealHighlight';

interface SeriesCardProps {
  series: any;
  groupId: string;
  userBet?: any;
  index: number;
  onRevealClick?: (seriesId: string) => void;
}

const NEW_WINDOW_MS = 48 * 60 * 60 * 1000;

function getCountdown(deadline: string): number | null {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours > 48) return null;
  return hours;
}

function formatHebrewDeadline(deadline: string): string {
  return new Intl.DateTimeFormat('he-IL', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(deadline));
}

function isFreshlyCreated(createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  const age = Date.now() - new Date(createdAt).getTime();
  return age >= 0 && age <= NEW_WINDOW_MS;
}

function GameDots({ wins, total = 4, isHome }: { wins: number; total?: number; isHome: boolean }) {
  return (
    <div className={`flex gap-1 ${isHome ? 'flex-row-reverse' : ''}`}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${i < wins ? 'bg-accent' : 'bg-border'}`}
        />
      ))}
    </div>
  );
}

const SeriesCard = ({ series, groupId, userBet, index, onRevealClick }: SeriesCardProps) => {
  const s = series;
  const isCompleted = s.status === 'completed';
  const isActive = s.status === 'in_progress';
  const isUpcoming = s.status === 'upcoming';
  const countdown = s.betting_deadline ? getCountdown(s.betting_deadline) : null;
  const isPastDeadline = s.betting_deadline && new Date(s.betting_deadline) < new Date();
  const isNew = isUpcoming && !isPastDeadline && !userBet && isFreshlyCreated(s.created_at);

  const canReveal = (isActive || isCompleted) && !!onRevealClick;
  const linkTo = isUpcoming && !isPastDeadline ? `/group/${groupId}/series/${s.id}` : undefined;
  const interactive = !!linkTo || canReveal;

  // Bet-result classification (only meaningful when the series resolved)
  const isExactHit = userBet?.is_correct_score === true;
  const isWinnerOnlyHit =
    userBet?.is_correct_winner === true && userBet?.is_correct_score === false;
  const isMiss =
    userBet?.is_correct_score === false && userBet?.is_correct_winner === false;

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className={`relative bg-card rounded-2xl overflow-hidden ${interactive ? 'cursor-pointer' : ''} ${
        canReveal
          ? 'px-5 pt-12 pb-5 border border-violet-400/40 shadow-[0_0_14px_rgba(167,139,250,0.15),inset_0_0_18px_rgba(167,139,250,0.08)]'
          : isNew
            ? 'p-5 border border-accent/60 ring-1 ring-accent/30 shadow-[0_0_18px_rgba(255,140,40,0.2),inset_0_0_12px_rgba(255,140,40,0.05)]'
            : 'p-5 border border-border card-elevated'
      }`}
    >
      {canReveal && <RevealHighlight variant="full" />}

      {isNew && (
        <div className="absolute top-2 right-2 z-[2] flex items-center gap-1 bg-accent text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,140,40,0.5)] animate-pulse">
          <Sparkles className="w-3 h-3" />
          חדש
        </div>
      )}

      {/* Teams row */}
      <div className="flex items-center justify-between overflow-hidden">
        {/* Home team */}
        <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
          <TeamLogo teamName={s.team_home} size={isCompleted ? 36 : 48} className="shrink-0 md:w-12 md:h-12" />
          <span className={`font-semibold truncate ${isCompleted ? 'text-[13px] md:text-sm' : 'text-sm'}`}>{s.team_home}</span>
        </div>

        {/* Center: VS or score */}
        <div className="flex flex-col items-center px-2 md:px-4 shrink-0">
          {isActive ? (
            <>
              <span className="text-2xl font-bold tracking-tighter" dir="ltr">
                {s.away_wins}-{s.home_wins}
              </span>
              <div className="flex gap-3 mt-1">
                <GameDots wins={s.home_wins || 0} isHome={true} />
                <GameDots wins={s.away_wins || 0} isHome={false} />
              </div>
            </>
          ) : isCompleted ? (
            <span className="text-base md:text-2xl font-bold tracking-tighter text-muted-foreground whitespace-nowrap" dir="ltr">
              {s.final_score ? s.final_score.split('-').reverse().join('-') : `${s.away_wins}-${s.home_wins}`}
            </span>
          ) : (
            <span className="text-sm font-medium text-muted-foreground">VS</span>
          )}
        </div>

        {/* Away team */}
        <div className="flex items-center gap-2 md:gap-3 flex-1 justify-end text-left min-w-0">
          <span className={`font-semibold truncate ${isCompleted ? 'text-[13px] md:text-sm' : 'text-sm'}`}>{s.team_away}</span>
          <TeamLogo teamName={s.team_away} size={isCompleted ? 36 : 48} className="shrink-0 md:w-12 md:h-12" />
        </div>
      </div>

      {/* Status row */}
      <div className="flex items-center justify-between mt-4 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0">
          {(isUpcoming || isActive) && isPastDeadline ? (
            <>
              <Lock className="w-3 h-3 text-red-400 shrink-0" />
              <span className="text-xs text-red-400 font-medium">ההימורים נסגרו</span>
            </>
          ) : isUpcoming && (
            <>
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
              <span className="text-xs text-green-400 font-medium">ההימורים פתוחים</span>
              {countdown !== null ? (
                <span className="text-xs text-orange-400 font-medium">נשארו {countdown} שעות להמר!</span>
              ) : s.betting_deadline && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatHebrewDeadline(s.betting_deadline)}
                </span>
              )}
            </>
          )}
          {isActive && !isPastDeadline && (
            <>
              <Zap className="w-4 h-4 text-accent shrink-0" />
              <span className="text-xs text-accent font-medium">בתהליך</span>
              {countdown !== null ? (
                <span className="text-xs text-orange-400 font-medium">נשארו {countdown} שעות להמר!</span>
              ) : s.betting_deadline && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatHebrewDeadline(s.betting_deadline)}
                </span>
              )}
            </>
          )}
          {isCompleted && (
            <>
              <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 shrink-0" />
              {s.winner && (
                <span className="text-[11px] md:text-xs text-accent font-bold truncate">{s.winner} ניצחה</span>
              )}
            </>
          )}
        </div>

        {/* User bet status */}
        {userBet ? (
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            {isExactHit ? (
              <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-1 rounded-full bg-green-500/20 text-green-400 font-semibold flex items-center gap-1 whitespace-nowrap">
                ✅ +{userBet.points_earned || 0} נק׳
              </span>
            ) : isWinnerOnlyHit ? (
              <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-1 rounded-full bg-yellow-500/15 text-yellow-400 font-semibold flex items-center gap-1 whitespace-nowrap">
                🏆 +{userBet.points_earned || 0} נק׳
              </span>
            ) : isMiss ? (
              <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-400">❌</span>
            ) : (
              <span className="text-[11px] md:text-xs text-muted-foreground whitespace-nowrap">
                הניחוש שלך: {userBet.predicted_score}
              </span>
            )}
          </div>
        ) : !isCompleted && !isPastDeadline ? (
          <span className="text-xs px-3 py-1 rounded-full border border-accent text-accent font-medium whitespace-nowrap">
            ממתין להימור
          </span>
        ) : null}
      </div>
    </motion.div>
  );

  if (linkTo) {
    return <Link to={linkTo} className="block mb-3">{cardContent}</Link>;
  }
  if (canReveal) {
    return (
      <button
        type="button"
        onClick={() => onRevealClick!(s.id)}
        className="block mb-3 w-full text-right"
      >
        {cardContent}
      </button>
    );
  }
  return <div className="mb-3">{cardContent}</div>;
};

export default SeriesCard;
